// modal/AlertProvider.tsx
import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useRef,
} from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';

import { Language } from '../Services/GlobalData';

type AlertType = 'success' | 'error' | 'warning';

interface AlertContextProps {
  showAlert: (
    type: AlertType,
    message: string,
    subHeader?: string,
    func?: () => void,
    useGif?: boolean,
  ) => void;
}

interface AlertState {
  type: AlertType;
  message: string;
  subHeader?: string;
  func?: () => void;
  visible: boolean;
  useGif?: boolean;
}

// ── PrimeIcons class per type ─────────────────────────────────────────────────
const ICON_CLASS: Record<AlertType, string> = {
  success: 'pi pi-check-circle',
  error: 'pi pi-times-circle',
  warning: 'pi pi-exclamation-triangle',
};

const ICON_COLOR: Record<AlertType, string> = {
  success: 'var(--color-green,  #009639)',
  error: 'var(--color-red,    #e30613)',
  warning: 'var(--color-orange, #f5a623)',
};

const MSG_COLOR: Record<AlertType, string> = {
  success: 'var(--color-text,   #333333)',
  error: 'var(--color-red,    #e30613)',
  warning: 'var(--color-orange, #f5a623)',
};

// ─────────────────────────────────────────────────────────────────────────────

const AlertContext = createContext<AlertContextProps | undefined>(undefined);

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {throw new Error('useAlert must be used within an AlertProvider');}
  return context;
};

export const AlertProvider = ({ children }: { children: ReactNode }) => {
  const [alert, setAlert] = useState<AlertState>({
    type: 'success',
    message: '',
    subHeader: '',
    func: undefined,
    visible: false,
    useGif: false,
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeAlert = useCallback(() => {
    setAlert(prev => ({
      ...prev,
      visible: false,
      message: '',
      subHeader: '',
      func: undefined,
      useGif: false,
    }));
  }, []);

  const showAlert = useCallback(
    (
      type: AlertType,
      message: string,
      subHeader?: string,
      func?: () => void,
      useGif = false,
    ) => {
      // Dismiss existing first, then re-show after short delay
      setAlert(prev => ({ ...prev, visible: false }));
      if (timeoutRef.current) {clearTimeout(timeoutRef.current);}

      timeoutRef.current = setTimeout(() => {
        setAlert({ type, message, subHeader, func, visible: true, useGif });
      }, 300);
    },
    [],
  );

  const alertPress = () => {
    const callback = alert.func;
    closeAlert();
    if (callback) {setTimeout(() => callback(), 300);}
  };

  // ── Icon: GIF for success+useGif, PrimeIcon otherwise ────────────────────
  const renderIcon = () => {
    if (alert.type === 'success' && alert.useGif) {
      return (
        <img
          src={require('../assets/images/Success1.gif')}
          alt="success"
          style={{ width: 100, height: 100, objectFit: 'contain', marginBottom: '0.5rem' }}
        />
      );
    }
    return (
      <i
        className={ICON_CLASS[alert.type]}
        style={{
          fontSize: '2.5rem',
          color: ICON_COLOR[alert.type],
          marginBottom: '0.75rem',
          marginTop: '0.25rem',
        }}
      />
    );
  };

  // ── Dialog footer: single centred OK button ───────────────────────────────
  const dialogFooter = (
    <div className="flex justify-content-center pb-2">
      <Button
        label={Language.Lbl_OK}
        onClick={alertPress}
        outlined
        pt={{
          root: {
            style: {
              borderColor: 'var(--color-lightgrey, #cccccc)',
              color: 'var(--color-text, #333333)',
              borderRadius: '12px',
              padding: '6px 30px',
              fontSize: '14px',
            },
          },
        }}
      />
    </div>
  );

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}

      <Dialog
        visible={alert.visible}
        onHide={closeAlert}
        footer={dialogFooter}
        // No close (✕) button — mirrors RN modal with no built-in dismiss button
        closable={false}
        draggable={false}
        resizable={false}
        modal
        // Clicking the mask/backdrop dismisses — mirrors RN onRequestClose
        dismissableMask
        style={{ width: '75vw', maxWidth: '360px' }}
        pt={{
          root: { style: { borderRadius: '35px', overflow: 'hidden' } },
          // Hide the title bar entirely (no header in the RN version)
          header: { style: { display: 'none' } },
          content: {
            style: {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              padding: '20px 20px 8px',
              borderRadius: '35px 35px 0 0',
            },
          },
          mask: {
            style: { backdropFilter: 'blur(1px)' },
          },
        }}
      >
        {/* Icon or success GIF */}
        {renderIcon()}

        {/* Main message */}
        <p
          style={{
            fontSize: '12px',
            color: MSG_COLOR[alert.type],
            textAlign: 'center',
            marginBottom: alert.subHeader ? '6px' : '18px',
            fontFamily: 'var(--font-medium)',
            margin: `0 0 ${alert.subHeader ? '6px' : '18px'}`,
          }}
        >
          {alert.message}
        </p>

        {/* Optional sub-header */}
        {alert.subHeader && (
          <p
            style={{
              fontSize: '14px',
              color: 'var(--color-text, #333333)',
              textAlign: 'center',
              margin: '0 0 12px',
              fontFamily: 'var(--font-medium)',
            }}
          >
            {alert.subHeader}
          </p>
        )}
      </Dialog>
    </AlertContext.Provider>
  );
};
